// client/src/pages/SettingsPage.jsx
import { useState, useEffect } from 'react'
import { User, Bell, BellOff, Check, Loader2, Building2, Search, Send } from 'lucide-react'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import { useAuth } from '../context/AuthContext'
import {
  useUpdateMe, useSubscribePush, useUnsubscribePush,
  useVehicles, useGarages, useGarageRequests, useSubmitGarageRequest,
} from '../api/hooks'
import api from '../api/client'

const TIMEZONES = [
  'Europe/London',
  'Europe/Dublin',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Amsterdam',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Australia/Sydney',
  'Asia/Dubai',
]

const inputCls = `w-full px-3 py-2 text-sm bg-[var(--bg-base)] border border-[var(--border)]
  rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)]
  focus:outline-none focus:border-indigo-500 transition-colors`

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64   = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw      = atob(base64)
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)))
}

// ─── Profile section ──────────────────────────────────────────────────────────

function ProfileSection({ user, login }) {
  const updateMe  = useUpdateMe()
  const [form, setForm] = useState({ name: user?.name || '', timezone: user?.timezone || 'Europe/London' })
  const [dirty, setDirty] = useState(false)

  const set = (field) => (e) => {
    setForm(p => ({ ...p, [field]: e.target.value }))
    setDirty(true)
  }

  const handleSave = async () => {
    try {
      const updated = await updateMe.mutateAsync({ name: form.name.trim(), timezone: form.timezone })
      login(localStorage.getItem('accessToken'), updated)
      toast.success('Profile updated')
      setDirty(false)
    } catch (err) {
      toast.error(err.response?.data?.error || 'Update failed')
    }
  }

  return (
    <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5">
      <div className="flex items-center gap-2 mb-5">
        <User size={16} className="text-[var(--text-muted)]" />
        <h2 className="font-medium text-[var(--text-primary)]">Profile</h2>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-xs text-[var(--text-muted)] mb-1.5">Email</label>
          <p className="px-3 py-2 text-sm text-[var(--text-secondary)] bg-[var(--bg-base)]
                        border border-[var(--border)] rounded-lg">
            {user?.email}
          </p>
        </div>

        <div>
          <label className="block text-xs text-[var(--text-muted)] mb-1.5">Name</label>
          <input
            type="text"
            value={form.name}
            onChange={set('name')}
            className={inputCls}
          />
        </div>

        <div>
          <label className="block text-xs text-[var(--text-muted)] mb-1.5">Timezone</label>
          <select
            value={form.timezone}
            onChange={set('timezone')}
            className={inputCls}
          >
            {TIMEZONES.map(tz => (
              <option key={tz} value={tz}>{tz.replace('_', ' ')}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center justify-between pt-1">
          <p className="text-xs text-[var(--text-muted)] capitalize">
            Role: {user?.role?.replace(/_/g, ' ')}
          </p>
          <button
            onClick={handleSave}
            disabled={!dirty || updateMe.isPending}
            className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700
                       disabled:opacity-40 text-white text-sm font-medium rounded-lg transition-colors"
          >
            {updateMe.isPending
              ? <Loader2 size={14} className="animate-spin" />
              : <Check size={14} />}
            Save
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Push notifications section ───────────────────────────────────────────────

function NotificationsSection() {
  const subscribePush   = useSubscribePush()
  const unsubscribePush = useUnsubscribePush()

  const [supported,     setSupported]     = useState(false)
  const [subscribed,    setSubscribed]    = useState(false)
  const [subscription,  setSubscription]  = useState(null)
  const [loading,       setLoading]       = useState(true)

  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setLoading(false)
      return
    }
    setSupported(true)

    navigator.serviceWorker.ready.then(reg => {
      reg.pushManager.getSubscription().then(sub => {
        setSubscription(sub)
        setSubscribed(!!sub)
        setLoading(false)
      })
    }).catch(() => setLoading(false))
  }, [])

  const handleSubscribe = async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/push/vapid-public-key')

      const reg = await navigator.serviceWorker.ready
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly:      true,
        applicationServerKey: urlBase64ToUint8Array(data.publicKey),
      })

      const json = sub.toJSON()
      await subscribePush.mutateAsync({
        endpoint: json.endpoint,
        p256dh:   json.keys.p256dh,
        auth:     json.keys.auth,
      })

      setSubscription(sub)
      setSubscribed(true)
      toast.success('Push notifications enabled')
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        toast.error('Notification permission denied. Please allow notifications in your browser settings.')
      } else {
        toast.error(err.response?.data?.error || 'Failed to enable notifications')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleUnsubscribe = async () => {
    setLoading(true)
    try {
      if (subscription) {
        await unsubscribePush.mutateAsync(subscription.endpoint)
        await subscription.unsubscribe()
      }
      setSubscription(null)
      setSubscribed(false)
      toast.success('Push notifications disabled')
    } catch (err) {
      toast.error('Failed to disable notifications')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5">
      <div className="flex items-center gap-2 mb-5">
        <Bell size={16} className="text-[var(--text-muted)]" />
        <h2 className="font-medium text-[var(--text-primary)]">Notifications</h2>
      </div>

      {!supported ? (
        <p className="text-sm text-[var(--text-muted)]">
          Push notifications are not supported in this browser.
        </p>
      ) : (
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-[var(--text-primary)]">
              Service reminders
            </p>
            <p className="text-xs text-[var(--text-muted)] mt-0.5">
              Receive push notifications when maintenance is due.
            </p>
          </div>

          <button
            onClick={subscribed ? handleUnsubscribe : handleSubscribe}
            disabled={loading}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium
                        transition-colors disabled:opacity-50 ${
              subscribed
                ? 'bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)] text-[var(--text-secondary)]'
                : 'bg-indigo-600 hover:bg-indigo-700 text-white'
            }`}
          >
            {loading
              ? <Loader2 size={14} className="animate-spin" />
              : subscribed
                ? <><BellOff size={14} /> Disable</>
                : <><Bell size={14} /> Enable</>}
          </button>
        </div>
      )}

      <p className="text-xs text-[var(--text-muted)] mt-4 border-t border-[var(--border)] pt-4">
        Email reminders are sent automatically based on your service schedule.
        Configure SMTP on the server to activate email delivery.
      </p>
    </div>
  )
}

// ─── My Garage section (customers only) ──────────────────────────────────────

const REQUEST_STATUS_STYLE = {
  pending:  'bg-amber-500/15 text-amber-400',
  approved: 'bg-green-500/15 text-green-400',
  declined: 'bg-red-500/15 text-red-400',
}
const REQUEST_STATUS_LABEL = {
  pending:  'Pending',
  approved: 'Registered',
  declined: 'Declined',
}

function MyGarageSection() {
  const { data: requests = [] }  = useGarageRequests()
  const { data: vehicles = [] }  = useVehicles()
  const submitRequest            = useSubmitGarageRequest()

  const [search, setSearch]                   = useState('')
  const [debounced, setDebounced]             = useState('')
  const [selectedGarage, setSelectedGarage]   = useState(null)
  const [selectedVehicleIds, setSelectedVehicleIds] = useState([])
  const [message, setMessage]                 = useState('')

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 350)
    return () => clearTimeout(t)
  }, [search])

  const { data: garageResults = [] } = useGarages(debounced)

  const toggleVehicle = (id) =>
    setSelectedVehicleIds(p => p.includes(id) ? p.filter(v => v !== id) : [...p, id])

  const handleSelectGarage = (g) => {
    setSelectedGarage(g)
    setSearch(g.name)
  }

  const handleSubmit = async () => {
    if (!selectedGarage || selectedVehicleIds.length === 0) return
    try {
      await submitRequest.mutateAsync({
        garageId:   selectedGarage.id,
        vehicleIds: selectedVehicleIds,
        message:    message.trim() || undefined,
      })
      toast.success('Request sent — the garage will be in touch.')
      setSelectedGarage(null)
      setSearch('')
      setSelectedVehicleIds([])
      setMessage('')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send request')
    }
  }

  const approvedGarageIds = new Set(
    requests.filter(r => r.status === 'approved').map(r => r.garage_id)
  )

  return (
    <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5">
      <div className="flex items-center gap-2 mb-5">
        <Building2 size={16} className="text-[var(--text-muted)]" />
        <h2 className="font-medium text-[var(--text-primary)]">My Garage</h2>
      </div>

      {/* Existing requests */}
      {requests.length > 0 && (
        <div className="space-y-2 mb-5">
          {requests.map(r => (
            <div key={r.id}
              className="flex items-center justify-between gap-3 px-4 py-3
                         bg-[var(--bg-base)] border border-[var(--border)] rounded-xl">
              <div className="min-w-0">
                <p className="text-sm font-medium text-[var(--text-primary)] truncate">
                  {r.garage_name}
                </p>
                {r.garage_address && (
                  <p className="text-xs text-[var(--text-muted)] truncate">{r.garage_address}</p>
                )}
                {r.status === 'declined' && (
                  <p className="text-xs text-[var(--text-muted)] mt-0.5">
                    You may re-submit a new request below.
                  </p>
                )}
              </div>
              <span className={clsx(
                'shrink-0 px-2 py-0.5 rounded-full text-xs font-medium',
                REQUEST_STATUS_STYLE[r.status]
              )}>
                {REQUEST_STATUS_LABEL[r.status]}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Registration form */}
      <div className="space-y-4">
        <div>
          <label className="block text-xs text-[var(--text-muted)] mb-1.5">
            {requests.some(r => r.status === 'approved')
              ? 'Register with another garage'
              : 'Search for a garage'}
          </label>

          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <input
              value={search}
              onChange={e => { setSearch(e.target.value); setSelectedGarage(null) }}
              placeholder="Type a garage name or town…"
              className={clsx(inputCls, 'pl-8')}
            />
          </div>

          {/* Search results */}
          {debounced && !selectedGarage && garageResults.length > 0 && (
            <div className="mt-1 bg-[var(--bg-base)] border border-[var(--border)] rounded-xl overflow-hidden">
              {garageResults.map(g => (
                <button
                  key={g.id}
                  onClick={() => handleSelectGarage(g)}
                  className="w-full flex flex-col items-start px-4 py-3 text-left
                             hover:bg-[var(--bg-overlay)] transition-colors
                             border-b border-[var(--border)] last:border-0"
                >
                  <p className="text-sm font-medium text-[var(--text-primary)]">{g.name}</p>
                  {g.address && (
                    <p className="text-xs text-[var(--text-muted)]">{g.address}</p>
                  )}
                  {approvedGarageIds.has(g.id) && (
                    <span className="mt-1 text-xs text-green-400">Already registered</span>
                  )}
                </button>
              ))}
            </div>
          )}

          {debounced && !selectedGarage && garageResults.length === 0 && (
            <p className="mt-2 text-xs text-[var(--text-muted)]">
              No garages found for "{debounced}".
            </p>
          )}
        </div>

        {/* Vehicle + message — shown after a garage is chosen */}
        {selectedGarage && (
          <>
            <div>
              <label className="block text-xs text-[var(--text-muted)] mb-2">
                Which vehicles would you like to register with{' '}
                <span className="text-[var(--text-secondary)]">{selectedGarage.name}</span>?
              </label>

              {vehicles.length === 0 ? (
                <p className="text-sm text-[var(--text-muted)]">
                  No vehicles found. Add a vehicle first.
                </p>
              ) : (
                <div className="space-y-2">
                  {vehicles.map(v => (
                    <label
                      key={v.id}
                      className="flex items-center gap-3 px-4 py-3 bg-[var(--bg-base)]
                                 border border-[var(--border)] rounded-xl cursor-pointer
                                 hover:bg-[var(--bg-overlay)] transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={selectedVehicleIds.includes(v.id)}
                        onChange={() => toggleVehicle(v.id)}
                        className="accent-indigo-500 shrink-0"
                      />
                      <div>
                        <p className="text-sm font-medium text-[var(--text-primary)]">
                          {v.make} {v.model}
                          {v.year && (
                            <span className="font-normal text-[var(--text-muted)]"> ({v.year})</span>
                          )}
                        </p>
                        {v.registration && (
                          <span className="inline-block font-mono font-bold tracking-widest text-xs
                                           bg-yellow-400 text-black px-1.5 py-0.5 rounded mt-0.5">
                            {v.registration}
                          </span>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-xs text-[var(--text-muted)] mb-1.5">
                Message to garage (optional)
              </label>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                rows={3}
                placeholder="Any additional information for the garage…"
                className={inputCls}
              />
            </div>

            <button
              onClick={handleSubmit}
              disabled={selectedVehicleIds.length === 0 || submitRequest.isPending}
              className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700
                         disabled:opacity-40 text-white text-sm font-medium rounded-lg transition-colors"
            >
              {submitRequest.isPending
                ? <><Loader2 size={14} className="animate-spin" /> Sending…</>
                : <><Send size={14} /> Send Request</>}
            </button>
          </>
        )}
      </div>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function SettingsPage() {
  const { user, login } = useAuth()

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => {})
    }
  }, [])

  return (
    <div className="space-y-6 max-w-lg">
      <h1 className="font-display text-3xl font-bold text-[var(--text-primary)]">Settings</h1>
      <ProfileSection user={user} login={login} />
      <NotificationsSection />
      {user?.role === 'customer' && <MyGarageSection />}
    </div>
  )
}
